/**
 * @file Légende d'horizon sous le calendrier de réservation.
 * @module packages.common.components.table-reservation.components.HorizonLegend
 *
 * Dit au client jusqu'où il peut réserver : fenêtre glissante (« jusqu'à X jours
 * à l'avance »), date butoir fixe (« ouvertes jusqu'au X ») ou calendrier fermé
 * (butoir dépassé). Le mode est lu par la source unique `bookingHorizon`, le
 * texte est traduit avec un repli français si `t` est absent.
 */

import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {format} from 'date-fns';

// @mui
import {alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// icons
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import {isFixedHorizon, isBookingClosed, getMaximumBookingDate} from '../utils/bookingHorizon';

/**
 * Encadré de légende d'horizon.
 *
 * @param {Object} props
 * @param {Object} props.bookingConstraints - Contraintes d'horizon (mode, fenêtre, butoir)
 * @param {Function} [props.t] - Fonction de traduction générique
 * @param {Object} [props.dateFnsLocale] - Locale date-fns courante
 * @return {JSX.Element}
 */
export default function HorizonLegend({bookingConstraints, t, dateFnsLocale}) {
  const legend = useMemo(() => {
    if (!isFixedHorizon(bookingConstraints)) {
      return {
        closed: false,
        html: t ?
          t('widgets.common.bookingLegend', {days: bookingConstraints.advanceBookingDays}) :
          `Réservations possibles jusqu'à <strong>${bookingConstraints.advanceBookingDays} jours</strong> à l'avance`,
      };
    }

    if (isBookingClosed(bookingConstraints)) {
      return {
        closed: true,
        html: t ?
          t('widgets.common.bookingTemporarilyClosed') :
          'Les réservations en ligne sont momentanément fermées',
      };
    }

    const openUntilLabel = format(
        getMaximumBookingDate(bookingConstraints),
        'd MMMM yyyy',
        dateFnsLocale ? {locale: dateFnsLocale} : undefined,
    );
    return {
      closed: false,
      html: t ?
        t('widgets.common.bookingLegendUntil', {date: openUntilLabel}) :
        `Réservations ouvertes jusqu'au <strong>${openUntilLabel}</strong>`,
    };
  }, [bookingConstraints, t, dateFnsLocale]);

  // Teinte du thème : orange pour un calendrier fermé, vert pour une fenêtre ouverte.
  const getColor = (theme) => (legend.closed ? theme.palette.orange.light : theme.palette.success.main);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        mt: 2,
        mb: 2,
        py: 1,
        px: 2,
        bgcolor: (theme) => alpha(getColor(theme), 0.08),
        border: '1px solid',
        borderColor: (theme) => alpha(getColor(theme), 0.2),
        borderRadius: 1.5,
      }}
    >
      {legend.closed ?
        <EventBusyIcon sx={{fontSize: 18, color: 'orange.light'}} /> :
        <EventAvailableIcon sx={{fontSize: 18, color: 'success.main'}} />}
      <Typography
        variant="caption"
        sx={{fontWeight: 500, color: 'text.primary'}}
        dangerouslySetInnerHTML={{__html: legend.html}}
      />
    </Box>
  );
}

HorizonLegend.propTypes = {
  bookingConstraints: PropTypes.object.isRequired,
  t: PropTypes.func,
  dateFnsLocale: PropTypes.object,
};
